import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, CheckCircle, XCircle, Mail, Phone, MapPin, Target,
  Plus, Edit, Trash2, Eye, Settings, BarChart3, FileText, Clock,
  AlertTriangle, TrendingUp, DollarSign, Briefcase, UserPlus, Search,
  Filter, RefreshCw, ChevronRight, Calendar, Activity, Inbox, Shield, LogOut
} from 'lucide-react';

/**
 * HIDDEN ADMIN DASHBOARD
 * 
 * This dashboard is NOT linked from public navigation.
 * Access only via secret URL: /tmg-admin-console-x7k9
 * 
 * Security:
 * - Requires admin role authentication
 * - No public Header shown (to hide navigation)
 * - Logout redirects to home (not admin login)
 */

// Color Palette
const colors = {
  sageTeal: '#97B6A7',
  softTeal: '#759B89',
  mutedTeal: '#587F6C',
  forestTeal: '#0E1E19',
  gold: '#D6B15E',
  goldHover: '#E2C77A',
  textHeading: '#FFFFFF',
  textBody: '#EAF2EF',
  textMuted: '#BFD0C9',
  cardLight: 'rgba(255, 255, 255, 0.92)',
};

const MainAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState({
    pendingApprovals: 0,
    totalCompanies: 0,
    totalNGOs: 0,
    activeEngagements: 0,
    totalBudget: 0,
    interestExpressions: 0
  });
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [approvedPartners, setApprovedPartners] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [interests, setInterests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [adminUser, setAdminUser] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Check admin authentication
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');
    
    if (!token || !userData) {
      // Redirect to hidden admin login
      navigate('/tmg-secure-portal-2025');
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== 'admin') {
      // Non-admin trying to access - redirect to home silently
      navigate('/');
      return;
    }

    setAdminUser(user);
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('xchange_token');
    
    try {
      // Fetch all data in parallel
      const [pendingRes, partnersRes, engagementsRes, interestsRes] = await Promise.all([
        fetch(`${backendUrl}/api/xchange/admin/pending-submissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/xchange/listings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/saas/engagements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/xchange/interests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const pending = pendingRes.ok ? await pendingRes.json() : [];
      const partners = partnersRes.ok ? await partnersRes.json() : [];
      const engagementsData = engagementsRes.ok ? await engagementsRes.json() : [];
      const interestsData = interestsRes.ok ? await interestsRes.json() : [];

      setPendingSubmissions(pending);
      setApprovedPartners(partners);
      setEngagements(engagementsData);
      setInterests(interestsData);

      // Calculate stats
      setStats({
        pendingApprovals: pending.length,
        totalCompanies: partners.filter(p => p.role === 'corporate').length,
        totalNGOs: partners.filter(p => p.role === 'ngo').length,
        activeEngagements: engagementsData.filter(e => e.status === 'active').length,
        totalBudget: engagementsData.reduce((sum, e) => sum + (e.totalBudget || 0), 0),
        interestExpressions: interestsData.length
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    if (!window.confirm('Approve this registration?')) return;
    
    try {
      const token = localStorage.getItem('xchange_token');
      const response = await fetch(`${backendUrl}/api/xchange/admin/approve/${submissionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Registration approved! Partner is now listed.');
        fetchAllData();
      } else {
        alert('Failed to approve');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Network error');
    }
  };

  const handleReject = async (submissionId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return;
    
    try {
      const token = localStorage.getItem('xchange_token');
      const response = await fetch(`${backendUrl}/api/xchange/admin/reject/${submissionId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        alert('Registration rejected');
        fetchAllData();
      } else {
        alert('Failed to reject');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Network error');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Delete this partner? This will remove them from listings.')) return;
    
    try {
      const token = localStorage.getItem('xchange_token');
      const response = await fetch(`${backendUrl}/api/xchange/admin/partner/${partnerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Partner deleted');
        fetchAllData();
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Network error');
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount?.toLocaleString() || 0}`;
  };

  const filteredPartners = approvedPartners.filter(p => {
    const matchesSearch = p.orgName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.contactName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || p.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Tab components
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard 
          icon={<Clock className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="Pending Approvals" 
          value={stats.pendingApprovals}
          onClick={() => setActiveTab('approvals')}
          highlight={stats.pendingApprovals > 0}
        />
        <StatCard 
          icon={<Building2 className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="Companies" 
          value={stats.totalCompanies}
          onClick={() => { setFilterRole('corporate'); setActiveTab('partners'); }}
        />
        <StatCard 
          icon={<Users className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="NGOs" 
          value={stats.totalNGOs}
          onClick={() => { setFilterRole('ngo'); setActiveTab('partners'); }}
        />
        <StatCard 
          icon={<Briefcase className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="Active Projects" 
          value={stats.activeEngagements}
          onClick={() => setActiveTab('engagements')}
        />
        <StatCard 
          icon={<DollarSign className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="Total Budget" 
          value={formatCurrency(stats.totalBudget)}
        />
        <StatCard 
          icon={<Inbox className="w-5 h-5" style={{ color: colors.gold }} />} 
          label="Interest Inquiries" 
          value={stats.interestExpressions}
          onClick={() => setActiveTab('interests')}
          highlight={stats.interestExpressions > 0}
        />
      </div>

      {/* Quick Actions */}
      <div 
        className="rounded-xl p-6"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.4)',
          border: '1px solid rgba(151, 182, 167, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: colors.textHeading }}>Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction 
            icon={<UserPlus />} 
            label="Add Partner" 
            onClick={() => navigate('/xchange/register/corporate')}
          />
          <QuickAction 
            icon={<Plus />} 
            label="Create Engagement" 
            onClick={() => setActiveTab('create-engagement')}
          />
          <QuickAction 
            icon={<Eye />} 
            label="View Marketplace" 
            onClick={() => navigate('/xchange')}
          />
          <QuickAction 
            icon={<BarChart3 />} 
            label="SaaS Dashboard" 
            onClick={() => navigate('/saas/admin/dashboard')}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Pending Approvals Preview */}
        <div 
          className="rounded-xl p-6"
          style={{ 
            backgroundColor: 'rgba(14, 30, 25, 0.4)',
            border: '1px solid rgba(151, 182, 167, 0.2)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold" style={{ color: colors.textHeading }}>Pending Approvals</h3>
            <button 
              onClick={() => setActiveTab('approvals')}
              className="text-sm flex items-center gap-1 transition-colors"
              style={{ color: colors.gold }}
            >
              View All <ChevronRight size={16} />
            </button>
          </div>
          {pendingSubmissions.length === 0 ? (
            <p className="text-center py-4" style={{ color: colors.textMuted }}>No pending approvals</p>
          ) : (
            <div className="space-y-3">
              {pendingSubmissions.slice(0, 3).map(sub => (
                <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', border: '1px solid rgba(151, 182, 167, 0.15)' }}>
                  <div className="flex items-center gap-3">
                    {sub.role === 'corporate' ? 
                      <Building2 className="w-5 h-5" style={{ color: colors.gold }} /> : 
                      <Users className="w-5 h-5" style={{ color: colors.gold }} />
                    }
                    <div>
                      <p className="font-medium" style={{ color: colors.textHeading }}>{sub.orgName}</p>
                      <p className="text-sm" style={{ color: colors.textMuted }}>{sub.role === 'corporate' ? 'Company' : 'NGO'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleApprove(sub.id)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
                    >
                      <CheckCircle size={18} style={{ color: colors.gold }} />
                    </button>
                    <button 
                      onClick={() => handleReject(sub.id)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
                    >
                      <XCircle size={18} style={{ color: '#EF4444' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Interest Expressions */}
        <div 
          className="rounded-xl p-6"
          style={{ 
            backgroundColor: 'rgba(14, 30, 25, 0.4)',
            border: '1px solid rgba(151, 182, 167, 0.2)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold" style={{ color: colors.textHeading }}>Recent Inquiries</h3>
            <button 
              onClick={() => setActiveTab('interests')}
              className="text-sm flex items-center gap-1 transition-colors"
              style={{ color: colors.gold }}
            >
              View All <ChevronRight size={16} />
            </button>
          </div>
          {interests.length === 0 ? (
            <p className="text-center py-4" style={{ color: colors.textMuted }}>No inquiries yet</p>
          ) : (
            <div className="space-y-3">
              {interests.slice(0, 3).map(interest => (
                <div key={interest.id} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', border: '1px solid rgba(151, 182, 167, 0.15)' }}>
                  <div className="flex justify-between">
                    <p className="font-medium" style={{ color: colors.textHeading }}>{interest.interested_party_name}</p>
                    <span className="text-xs" style={{ color: colors.textMuted }}>
                      {new Date(interest.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm truncate" style={{ color: colors.textMuted }}>{interest.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ApprovalsTab = () => (
    <div 
      className="rounded-xl overflow-hidden"
      style={{ 
        backgroundColor: 'rgba(14, 30, 25, 0.4)',
        border: '1px solid rgba(151, 182, 167, 0.2)',
        backdropFilter: 'blur(8px)'
      }}
    >
      <div className="p-6" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
        <h3 className="text-lg font-semibold" style={{ color: colors.textHeading }}>Pending Registration Approvals</h3>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Review and approve/reject new partner registrations</p>
      </div>
      
      {pendingSubmissions.length === 0 ? (
        <div className="p-12 text-center">
          <CheckCircle className="mx-auto mb-4" size={48} style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>All registrations have been processed!</p>
        </div>
      ) : (
        <div>
          {pendingSubmissions.map(sub => (
            <div key={sub.id} className="p-6" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.15)' }}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}>
                    {sub.role === 'corporate' ? 
                      <Building2 className="w-6 h-6" style={{ color: colors.gold }} /> : 
                      <Users className="w-6 h-6" style={{ color: colors.gold }} />
                    }
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg" style={{ color: colors.textHeading }}>{sub.orgName}</h4>
                    <span 
                      className="inline-block px-2 py-0.5 rounded text-xs font-medium mt-1"
                      style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}
                    >
                      {sub.role === 'corporate' ? 'Company' : 'NGO/Agency'}
                    </span>
                    
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2" style={{ color: colors.textMuted }}>
                        <Mail size={14} />
                        {sub.email}
                      </div>
                      {sub.phone && (
                        <div className="flex items-center gap-2" style={{ color: colors.textMuted }}>
                          <Phone size={14} />
                          {sub.phone}
                        </div>
                      )}
                      {sub.states?.length > 0 && (
                        <div className="flex items-center gap-2" style={{ color: colors.textMuted }}>
                          <MapPin size={14} />
                          {sub.states.slice(0, 2).join(', ')}{sub.states.length > 2 && '...'}
                        </div>
                      )}
                      {sub.sectors?.length > 0 && (
                        <div className="flex items-center gap-2" style={{ color: colors.textMuted }}>
                          <Target size={14} />
                          {sub.sectors.slice(0, 2).join(', ')}{sub.sectors.length > 2 && '...'}
                        </div>
                      )}
                    </div>
                    
                    {sub.description && (
                      <p className="mt-3 text-sm" style={{ color: colors.textMuted }}>{sub.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleApprove(sub.id)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300"
                    style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
                  >
                    <CheckCircle size={16} />
                    Approve
                  </button>
                  <button 
                    onClick={() => handleReject(sub.id)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300"
                    style={{ backgroundColor: 'transparent', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.5)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#EF4444'; e.currentTarget.style.color = '#FFFFFF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#EF4444'; }}
                  >
                    <XCircle size={16} />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const PartnersTab = () => (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div 
        className="rounded-xl p-4 flex flex-wrap gap-4 items-center"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.4)',
          border: '1px solid rgba(151, 182, 167, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={18} style={{ color: colors.textMuted }} />
          <input
            type="text"
            placeholder="Search partners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none transition-all"
            style={{ 
              backgroundColor: 'rgba(14, 30, 25, 0.6)',
              border: '1px solid rgba(151, 182, 167, 0.3)',
              color: colors.textBody
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} style={{ color: colors.textMuted }} />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm outline-none cursor-pointer"
            style={{ 
              backgroundColor: 'rgba(14, 30, 25, 0.6)',
              border: '1px solid rgba(151, 182, 167, 0.3)',
              color: colors.textBody
            }}
          >
            <option value="all">All Partners</option>
            <option value="corporate">Companies Only</option>
            <option value="ngo">NGOs Only</option>
          </select>
        </div>
        <button
          onClick={() => navigate('/xchange/register/corporate')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300"
          style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
        >
          <Plus size={18} />
          Add Partner
        </button>
      </div>

      {/* Partners List */}
      <div 
        className="rounded-xl overflow-hidden"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.4)',
          border: '1px solid rgba(151, 182, 167, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <table className="w-full">
          <thead style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Organization</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Type</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Contact</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Sectors</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPartners.map(partner => (
              <tr key={partner.id} style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.1)' }} className="hover:bg-white/5">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {partner.role === 'corporate' ? 
                      <Building2 className="w-5 h-5" style={{ color: colors.gold }} /> : 
                      <Users className="w-5 h-5" style={{ color: colors.gold }} />
                    }
                    <div>
                      <p className="font-medium" style={{ color: colors.textHeading }}>{partner.orgName}</p>
                      <p className="text-sm" style={{ color: colors.textMuted }}>{partner.contactName}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}>
                    {partner.role === 'corporate' ? 'Company' : 'NGO'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm" style={{ color: colors.textBody }}>{partner.email}</p>
                  {partner.phone && <p className="text-sm" style={{ color: colors.textMuted }}>{partner.phone}</p>}
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {partner.sectors?.slice(0, 2).join(', ')}
                    {partner.sectors?.length > 2 && '...'}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/admin/partner/${partner.id}`)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
                      title="View/Edit"
                    >
                      <Edit size={16} style={{ color: colors.gold }} />
                    </button>
                    <button 
                      onClick={() => handleDeletePartner(partner.id)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}
                      title="Delete"
                    >
                      <Trash2 size={16} style={{ color: '#EF4444' }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredPartners.length === 0 && (
          <div className="p-12 text-center" style={{ color: colors.textMuted }}>
            No partners found matching your criteria
          </div>
        )}
      </div>
    </div>
  );

  const EngagementsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold" style={{ color: colors.textHeading }}>SaaS Engagements</h3>
        <button
          onClick={() => setActiveTab('create-engagement')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300"
          style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
        >
          <Plus size={18} />
          Create Engagement
        </button>
      </div>

      <div 
        className="rounded-xl overflow-hidden"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.4)',
          border: '1px solid rgba(151, 182, 167, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <table className="w-full">
          <thead style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Project</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Company</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>NGO</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Budget</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Status</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Health</th>
              <th className="text-left px-6 py-3 text-sm font-semibold" style={{ color: colors.textMuted }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {engagements.map(eng => (
              <tr key={eng.id} style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.1)' }} className="hover:bg-white/5">
                <td className="px-6 py-4">
                  <p className="font-medium" style={{ color: colors.textHeading }}>{eng.projectTitle}</p>
                  <p className="text-sm" style={{ color: colors.textMuted }}>{eng.sector}</p>
                </td>
                <td className="px-6 py-4 text-sm" style={{ color: colors.textBody }}>{eng.companyName}</td>
                <td className="px-6 py-4 text-sm" style={{ color: colors.textBody }}>{eng.agencyName}</td>
                <td className="px-6 py-4 text-sm font-medium" style={{ color: colors.textHeading }}>
                  {formatCurrency(eng.totalBudget)}
                </td>
                <td className="px-6 py-4">
                  <span 
                    className="px-2 py-1 rounded text-xs font-medium capitalize"
                    style={{ 
                      backgroundColor: eng.status === 'active' ? 'rgba(214, 177, 94, 0.2)' : 'rgba(151, 182, 167, 0.2)',
                      color: eng.status === 'active' ? colors.gold : colors.textMuted
                    }}
                  >
                    {eng.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="flex items-center gap-1 text-sm">
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ 
                        backgroundColor: eng.healthStatus === 'on_track' ? colors.gold : 
                                        eng.healthStatus === 'at_risk' ? '#F59E0B' : '#EF4444'
                      }}
                    />
                    <span style={{ color: colors.textMuted }}>{eng.healthStatus?.replace('_', ' ')}</span>
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/engagement/${eng.id}`)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
                      title="View Details"
                    >
                      <Eye size={16} style={{ color: colors.gold }} />
                    </button>
                    <button 
                      onClick={() => navigate(`/admin/engagement/${eng.id}/edit`)}
                      className="p-2 rounded-lg transition-all"
                      style={{ backgroundColor: 'rgba(151, 182, 167, 0.15)' }}
                      title="Edit"
                    >
                      <Edit size={16} style={{ color: colors.textMuted }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {engagements.length === 0 && (
          <div className="p-12 text-center" style={{ color: colors.textMuted }}>
            No engagements created yet. Click "Create Engagement" to get started.
          </div>
        )}
      </div>
    </div>
  );

  const InterestsTab = () => (
    <div 
      className="rounded-xl overflow-hidden"
      style={{ 
        backgroundColor: 'rgba(14, 30, 25, 0.4)',
        border: '1px solid rgba(151, 182, 167, 0.2)',
        backdropFilter: 'blur(8px)'
      }}
    >
      <div className="p-6" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
        <h3 className="text-lg font-semibold" style={{ color: colors.textHeading }}>Interest Expressions / Inquiries</h3>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Leads from visitors interested in your listings</p>
      </div>
      
      {interests.length === 0 ? (
        <div className="p-12 text-center">
          <Inbox className="mx-auto mb-4" size={48} style={{ color: colors.textMuted }} />
          <p style={{ color: colors.textBody }}>No inquiries received yet</p>
        </div>
      ) : (
        <div>
          {interests.map(interest => (
            <div key={interest.id} className="p-6" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.15)' }}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold" style={{ color: colors.textHeading }}>{interest.interested_party_name}</h4>
                  <div className="flex items-center gap-4 mt-2 text-sm" style={{ color: colors.textMuted }}>
                    <span className="flex items-center gap-1">
                      <Mail size={14} />
                      {interest.interested_party_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(interest.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="mt-3" style={{ color: colors.textBody }}>{interest.message}</p>
                  {interest.listing_name && (
                    <p className="mt-2 text-sm">
                      <span style={{ color: colors.textMuted }}>Interested in:</span>{' '}
                      <span className="font-medium" style={{ color: colors.gold }}>{interest.listing_name}</span>
                    </p>
                  )}
                </div>
                <a
                  href={`mailto:${interest.interested_party_email}?subject=Regarding your inquiry on TMG Impact`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300"
                  style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
                >
                  <Mail size={16} />
                  Reply
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const CreateEngagementTab = () => {
    const [formData, setFormData] = useState({
      projectTitle: '',
      description: '',
      companyId: '',
      agencyId: '',
      totalBudget: '',
      sector: '',
      state: '',
      startDate: '',
      endDate: ''
    });
    const [creating, setCreating] = useState(false);

    const companies = approvedPartners.filter(p => p.role === 'corporate');
    const ngos = approvedPartners.filter(p => p.role === 'ngo');

    const handleCreate = async (e) => {
      e.preventDefault();
      setCreating(true);
      
      try {
        const token = localStorage.getItem('xchange_token');
        const selectedCompany = companies.find(c => c.id === formData.companyId);
        const selectedNGO = ngos.find(n => n.id === formData.agencyId);

        const response = await fetch(`${backendUrl}/api/saas/engagements`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            companyName: selectedCompany?.orgName,
            agencyName: selectedNGO?.orgName,
            totalBudget: parseFloat(formData.totalBudget),
            status: 'active',
            healthStatus: 'on_track'
          })
        });

        if (response.ok) {
          alert('Engagement created successfully!');
          fetchAllData();
          setActiveTab('engagements');
        } else {
          const error = await response.json();
          alert(error.detail || 'Failed to create engagement');
        }
      } catch (err) {
        console.error('Error:', err);
        alert('Network error');
      } finally {
        setCreating(false);
      }
    };

    const inputStyle = { 
      backgroundColor: 'rgba(14, 30, 25, 0.6)', 
      border: '1px solid rgba(151, 182, 167, 0.3)', 
      color: colors.textBody 
    };

    return (
      <div 
        className="rounded-xl p-6 max-w-3xl"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.4)',
          border: '1px solid rgba(151, 182, 167, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <h3 className="text-lg font-semibold mb-6" style={{ color: colors.textHeading }}>Create New SaaS Engagement</h3>
        
        <form onSubmit={handleCreate} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Project Title *</label>
            <input
              type="text"
              required
              value={formData.projectTitle}
              onChange={(e) => setFormData({...formData, projectTitle: e.target.value})}
              className="w-full px-4 py-2 rounded-lg outline-none transition-all"
              style={inputStyle}
              placeholder="e.g., Digital Literacy for Rural Women"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Company (Sponsor) *</label>
              <select
                required
                value={formData.companyId}
                onChange={(e) => setFormData({...formData, companyId: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none cursor-pointer"
                style={inputStyle}
              >
                <option value="">Select Company</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.orgName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>NGO (Agency) *</label>
              <select
                required
                value={formData.agencyId}
                onChange={(e) => setFormData({...formData, agencyId: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none cursor-pointer"
                style={inputStyle}
              >
                <option value="">Select NGO</option>
                {ngos.map(n => (
                  <option key={n.id} value={n.id}>{n.orgName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Total Budget (₹) *</label>
              <input
                type="number"
                required
                value={formData.totalBudget}
                onChange={(e) => setFormData({...formData, totalBudget: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                placeholder="2500000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Start Date *</label>
              <input
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>End Date *</label>
              <input
                type="date"
                required
                value={formData.endDate}
                onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Sector</label>
              <select
                value={formData.sector}
                onChange={(e) => setFormData({...formData, sector: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none cursor-pointer"
                style={inputStyle}
              >
                <option value="">Select Sector</option>
                <option value="Education">Education</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Livelihood">Livelihood</option>
                <option value="Environment">Environment</option>
                <option value="Women Empowerment">Women Empowerment</option>
                <option value="Rural Development">Rural Development</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                placeholder="Maharashtra"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textHeading }}>Description</label>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 rounded-lg outline-none resize-none"
              style={inputStyle}
              placeholder="Brief description of the project..."
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={creating}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50"
              style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
              onMouseEnter={(e) => { if (!creating) { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
            >
              {creating ? <RefreshCw className="animate-spin" size={18} /> : <Plus size={18} />}
              {creating ? 'Creating...' : 'Create Engagement'}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('engagements')}
              className="px-6 py-3 rounded-lg font-semibold transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.textBody, border: '1.5px solid rgba(255, 255, 255, 0.35)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textBody; }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Helper components
  const StatCard = ({ icon, label, value, onClick, highlight }) => (
    <div 
      onClick={onClick}
      className={`rounded-xl p-4 cursor-pointer transition-all duration-300 ${highlight ? 'ring-2' : ''}`}
      style={{ 
        backgroundColor: 'rgba(14, 30, 25, 0.4)',
        border: '1px solid rgba(151, 182, 167, 0.2)',
        backdropFilter: 'blur(8px)',
        ringColor: highlight ? colors.gold : 'transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.gold;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}>{icon}</div>
        <div>
          <p className="text-2xl font-bold" style={{ color: colors.textHeading }}>{value}</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>{label}</p>
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ icon, label, onClick }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-3 p-4 rounded-lg transition-all duration-300 text-left"
      style={{ 
        backgroundColor: 'rgba(14, 30, 25, 0.5)',
        border: '1px solid rgba(151, 182, 167, 0.2)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.gold;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)', color: colors.gold }}>{icon}</div>
      <span className="font-medium" style={{ color: colors.textBody }}>{label}</span>
    </button>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'approvals', label: 'Approvals', icon: <Clock size={18} />, badge: stats.pendingApprovals },
    { id: 'partners', label: 'Partners', icon: <Users size={18} /> },
    { id: 'engagements', label: 'Engagements', icon: <Briefcase size={18} /> },
    { id: 'interests', label: 'Inquiries', icon: <Inbox size={18} />, badge: stats.interestExpressions },
  ];

  const handleLogout = () => {
    localStorage.removeItem('xchange_token');
    localStorage.removeItem('xchange_user');
    navigate('/');
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${colors.sageTeal} 0%, ${colors.softTeal} 35%, ${colors.mutedTeal} 70%, ${colors.forestTeal} 100%)`
        }}
      >
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={40} style={{ color: colors.gold }} />
          <p style={{ color: colors.textMuted }}>Loading admin console...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: `linear-gradient(135deg, ${colors.sageTeal} 0%, ${colors.softTeal} 35%, ${colors.mutedTeal} 70%, ${colors.forestTeal} 100%)`
      }}
    >
      {/* Custom Admin Header - No public navigation */}
      <header 
        className="sticky top-0 z-50"
        style={{ 
          backgroundColor: 'rgba(14, 30, 25, 0.9)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(151, 182, 167, 0.15)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
            >
              <Shield className="w-5 h-5" style={{ color: colors.gold }} />
            </div>
            <div>
              <h1 className="font-bold" style={{ color: colors.textHeading }}>TMG Impact</h1>
              <p className="text-xs" style={{ color: colors.textMuted }}>Admin Console</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium" style={{ color: colors.textHeading }}>{adminUser?.orgName || 'Admin'}</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>{adminUser?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300"
              style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', color: colors.textBody, border: '1px solid rgba(151, 182, 167, 0.3)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(14, 30, 25, 0.5)'; e.currentTarget.style.color = colors.textBody; }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: colors.textHeading }}>Admin Control Panel</h1>
          <p className="mt-1" style={{ color: colors.textBody }}>Manage partners, engagements, and platform operations</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 pb-4" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300"
              style={{ 
                backgroundColor: activeTab === tab.id ? '#0E1E19' : 'transparent',
                color: activeTab === tab.id ? colors.textHeading : colors.textBody,
                border: activeTab === tab.id ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = colors.gold;
                  e.currentTarget.style.color = '#0E1E19';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = colors.textBody;
                }
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.badge > 0 && (
                <span 
                  className="ml-1 px-2 py-0.5 rounded-full text-xs"
                  style={{ 
                    backgroundColor: activeTab === tab.id ? colors.gold : colors.gold,
                    color: '#0E1E19'
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'approvals' && <ApprovalsTab />}
        {activeTab === 'partners' && <PartnersTab />}
        {activeTab === 'engagements' && <EngagementsTab />}
        {activeTab === 'interests' && <InterestsTab />}
        {activeTab === 'create-engagement' && <CreateEngagementTab />}
      </div>
    </div>
  );
};

export default MainAdminDashboard;
